import { useState, useEffect } from 'react'
import { api } from '@/services/api-client'
import { useAvailableModels } from '@/hooks/queries'
import {
  Share2, Plus, Trash2, X, ChevronRight, Loader2, Sparkles, Tag, ArrowLeft,
  Check, XCircle, Clock, Eye, Edit3, Facebook, Instagram, Calendar, Target,
  Link as LinkIcon, Globe, Send, Image as ImageIcon, Filter, FolderOpen,
  Settings, Wifi, WifiOff, Linkedin, MessageCircle, ShoppingCart
} from 'lucide-react'
import { getModelShortName } from '@/utils/helpers'
import { useUIStore } from '@/stores'

// ============ Types ============

type SocialAccount = {
  id: string; platform: string; account_name: string; login: string;
  profile_url: string; status: string; created_at: string
}

type SocialProject = {
  id: string; name: string; description: string; icon_color: string;
  accounts: SocialAccount[]; campaigns: Campaign[]; created_at: string
}

type Campaign = {
  id: string; name: string; topic: string; description: string;
  keywords: string[]; info_sources: string[]; target_count: number;
  goal: string; platforms: string[]; schedule_frequency: string;
  schedule_time_mode: string; schedule_fixed_hour: string;
  schedule_range_start: string; schedule_range_end: string;
  provider: string; model: string; status: string;
  social_project_id: string; project_name: string;
  generated_count: number; approved_count: number; published_count: number;
  created_at: string; posts?: Post[]
}

type Post = {
  id: string; campaign_id: string; platform: string; content: string;
  hashtags: string[]; image_prompt: string; image_url: string;
  status: string; rejection_reason: string;
  scheduled_for: string | null; published_at: string | null; sort_order: number
}

// ============ Constants ============

const GOAL_OPTIONS = [
  { value: 'seo', label: 'SEO & Pozycjonowanie', icon: '🔍', desc: 'Optymalizacja pod wyszukiwarki' },
  { value: 'prestige', label: 'Status eksperta', icon: '🏆', desc: 'Budowanie autorytetu' },
  { value: 'engagement', label: 'Zaangażowanie', icon: '💬', desc: 'Dyskusje i interakcje' },
  { value: 'brand_awareness', label: 'Świadomość marki', icon: '📢', desc: 'Rozpoznawalność i zasięg' },
]

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Codziennie' }, { value: 'every_few_days', label: 'Co kilka dni' },
  { value: 'weekly', label: 'Co tydzień' }, { value: 'every_few_weeks', label: 'Co kilka tygodni' },
  { value: 'monthly', label: 'Co miesiąc' },
]

const PLATFORM_OPTIONS = [
  { value: 'blog_api', label: 'Blog (REST API)', color: '#10b981' },
  { value: 'facebook', label: 'Facebook', color: '#1877f2' },
  { value: 'instagram', label: 'Instagram', color: '#e4405f' },
  { value: 'tiktok', label: 'TikTok', color: '#010101' },
  { value: 'linkedin', label: 'LinkedIn', color: '#0a66c2' },
  { value: 'x', label: 'X (Twitter)', color: '#1da1f2' },
  { value: 'pinterest', label: 'Pinterest', color: '#e60023' },
  { value: 'reddit', label: 'Reddit', color: '#ff4500' },
  { value: 'discord', label: 'Discord', color: '#5865f2' },
  { value: 'telegram', label: 'Telegram', color: '#26a5e4' },
]

const STATUS_LABELS: Record<string, string> = {
  pending: 'Do akceptacji', approved: 'Zaakceptowany', rejected: 'Odrzucony',
  published: 'Opublikowany', scheduled: 'Zaplanowany',
}

function getPlatformIcon(platform: string, size = 14) {
  const color = PLATFORM_OPTIONS.find(p => p.value === platform)?.color || '#666'
  switch (platform) {
    case 'blog_api': return <Globe size={size} style={{ color }} />
    case 'facebook': return <Facebook size={size} style={{ color }} />
    case 'instagram': return <Instagram size={size} style={{ color }} />
    case 'linkedin': return <Linkedin size={size} style={{ color }} />
    case 'discord': case 'telegram': return <MessageCircle size={size} style={{ color }} />
    default: return <Globe size={size} style={{ color }} />
  }
}

function getPlatformColor(platform: string) {
  return PLATFORM_OPTIONS.find(p => p.value === platform)?.color || '#666'
}

// ============ Main Component ============

export function SocialAgentView() {
  const { data: availableModels } = useAvailableModels()
  const navigateToMarketplace = useUIStore(s => s.navigateToMarketplace)
  const hasProviders = availableModels && availableModels.length > 0

  // Data
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [projects, setProjects] = useState<SocialProject[]>([])
  const [accounts, setAccounts] = useState<SocialAccount[]>([])
  const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null)
  const [activeProject, setActiveProject] = useState<SocialProject | null>(null)

  // UI state
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [reviewPost, setReviewPost] = useState<Post | null>(null)
  const [editingPost, setEditingPost] = useState<Post | null>(null)
  const [editContent, setEditContent] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')
  const [showManageAccounts, setShowManageAccounts] = useState(false)

  // ---- Wizard state ----
  const [wizardStep, setWizardStep] = useState<null | 'accounts' | 'project' | 'campaign'>(null)
  const [wizardAccounts, setWizardAccounts] = useState<string[]>([])  // selected account IDs
  const [wizardProjectId, setWizardProjectId] = useState<string | null>(null)
  const [newProjectName, setNewProjectName] = useState('')

  // Account form
  const [addAccountPlatform, setAddAccountPlatform] = useState('blog_api')
  const [addAccountName, setAddAccountName] = useState('')
  const [addAccountLogin, setAddAccountLogin] = useState('')
  const [addAccountPassword, setAddAccountPassword] = useState('')
  const [addAccountUrl, setAddAccountUrl] = useState('')
  const [addAccountApiKey, setAddAccountApiKey] = useState('')
  const [testingAccount, setTestingAccount] = useState<string | null>(null)

  // Campaign form
  const [keywordInput, setKeywordInput] = useState('')
  const [sourceInput, setSourceInput] = useState('')
  const [form, setForm] = useState({
    topic: '', description: '', keywords: [] as string[],
    info_sources: [] as string[], target_count: '5',
    goal: 'seo', platforms: [] as string[],
    schedule_frequency: 'daily', schedule_time_mode: 'fixed',
    schedule_fixed_hour: '10:00',
    schedule_range_start: '09:00', schedule_range_end: '17:00',
    provider: '', model: '',
  })

  const selectedProviderModels = availableModels?.find(p => p.provider_type === form.provider)?.models || []

  useEffect(() => {
    if (availableModels && availableModels.length > 0 && !form.provider) {
      const first = availableModels[0]
      setForm(f => ({ ...f, provider: first.provider_type, model: first.models[0] || '' }))
    }
  }, [availableModels])

  // ============ Data fetching ============

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [c, p, a] = await Promise.all([
        api.getSocialCampaigns(), api.getSocialProjects(), api.getSocialAccounts()
      ])
      setCampaigns(c); setProjects(p); setAccounts(a)
    } catch { /* */ } finally { setLoading(false) }
  }

  const fetchCampaign = async (id: string) => {
    setLoading(true)
    try { setActiveCampaign(await api.getSocialCampaign(id)) } catch { /* */ } finally { setLoading(false) }
  }

  const fetchProject = async (id: string) => {
    try { setActiveProject(await api.getSocialProject(id)) } catch { /* */ }
  }

  useEffect(() => { fetchAll() }, [])

  // ============ Account actions ============

  const handleAddAccount = async () => {
    if (!addAccountName.trim()) return
    try {
      const acc = await api.connectSocialAccount({
        platform: addAccountPlatform, account_name: addAccountName.trim(),
        login: addAccountLogin, password: addAccountPassword,
        access_token: addAccountPlatform === 'blog_api' ? addAccountApiKey : '',
        profile_url: addAccountUrl
      })
      setAddAccountName(''); setAddAccountLogin(''); setAddAccountPassword(''); setAddAccountUrl(''); setAddAccountApiKey('')
      setAccounts(prev => [acc, ...prev])
    } catch { /* */ }
  }

  const handleTestAccount = async (id: string) => {
    setTestingAccount(id)
    try {
      await api.testSocialAccount(id)
      setAccounts(prev => prev.map(a => a.id === id ? { ...a, status: 'connected' } : a))
    } catch {
      setAccounts(prev => prev.map(a => a.id === id ? { ...a, status: 'error' } : a))
    } finally { setTestingAccount(null) }
  }

  const handleDeleteAccount = async (id: string) => {
    try {
      await api.disconnectSocialAccount(id)
      setAccounts(prev => prev.filter(a => a.id !== id))
    } catch { /* */ }
  }

  // ============ Keyword handling (comma trigger) ============

  const handleKeywordInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (val.endsWith(',')) {
      const kw = val.slice(0, -1).trim()
      if (kw && !form.keywords.includes(kw)) {
        setForm(f => ({ ...f, keywords: [...f.keywords, kw] }))
      }
      setKeywordInput('')
    } else {
      setKeywordInput(val)
    }
  }

  const handleKeywordKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const kw = keywordInput.trim()
      if (kw && !form.keywords.includes(kw)) {
        setForm(f => ({ ...f, keywords: [...f.keywords, kw] }))
      }
      setKeywordInput('')
    }
  }

  const handleAddSource = () => {
    const src = sourceInput.trim()
    if (src && !form.info_sources.includes(src)) {
      setForm(f => ({ ...f, info_sources: [...f.info_sources, src] }))
      setSourceInput('')
    }
  }

  // ============ Wizard actions ============

  const startWizard = () => {
    setWizardStep('accounts')
    setWizardAccounts([])
    setWizardProjectId(null)
    setNewProjectName('')
    setForm({ topic: '', description: '', keywords: [], info_sources: [], target_count: '5', goal: 'seo', platforms: [], schedule_frequency: 'daily', schedule_time_mode: 'fixed', schedule_fixed_hour: '10:00', schedule_range_start: '09:00', schedule_range_end: '17:00', provider: 'openai', model: 'gpt-4o' })
  }

  const startWizardFromProject = (project: SocialProject) => {
    // Pre-select project and its accounts, go to accounts step to allow adding more
    const accountIds = project.accounts?.map(a => a.id) || []
    const platforms = [...new Set(project.accounts?.map(a => a.platform) || [])]
    setWizardAccounts(accountIds)
    setWizardProjectId(project.id)
    setNewProjectName('')
    setForm({ topic: '', description: '', keywords: [], info_sources: [], target_count: '5', goal: 'seo', platforms, schedule_frequency: 'daily', schedule_time_mode: 'fixed', schedule_fixed_hour: '10:00', schedule_range_start: '09:00', schedule_range_end: '17:00', provider: 'openai', model: 'gpt-4o' })
    setActiveProject(null)
    setWizardStep('accounts')
  }

  const wizardGoToProject = () => {
    // Derive platforms from selected accounts
    const selectedAccounts = accounts.filter(a => wizardAccounts.includes(a.id))
    const platforms = [...new Set(selectedAccounts.map(a => a.platform))]
    setForm(f => ({ ...f, platforms }))
    setWizardStep('project')
  }

  const wizardGoToCampaign = async () => {
    // If creating new project
    if (!wizardProjectId && newProjectName.trim()) {
      try {
        const proj = await api.createSocialProject({ name: newProjectName.trim(), account_ids: wizardAccounts })
        setWizardProjectId(proj.id)
        await fetchAll()
      } catch { /* */ }
    } else if (wizardProjectId) {
      // Update project to link selected accounts
      try {
        await api.updateSocialProject(wizardProjectId, { account_ids: wizardAccounts })
      } catch { /* */ }
    }
    setWizardStep('campaign')
  }

  const handleCreateCampaign = async () => {
    if (!form.topic.trim()) return
    try {
      const campaign = await api.createSocialCampaign({
        ...form, target_count: parseInt(form.target_count) || 5,
        social_project_id: wizardProjectId || undefined,
      })
      setWizardStep(null)
      setActiveCampaign(campaign)
      fetchAll()
    } catch { /* */ }
  }

  // ============ Campaign/Post actions ============

  const handleGenerate = async (campaignId: string) => {
    setGenerating(true)
    try { await api.generateSocialPosts(campaignId); await fetchCampaign(campaignId); fetchAll() }
    catch { /* */ } finally { setGenerating(false) }
  }

  const handleApprove = async (postId: string) => {
    try {
      await api.approveSocialPost(postId)
      if (activeCampaign) await fetchCampaign(activeCampaign.id)
      setReviewPost(null)
    } catch { /* */ }
  }

  const handleReject = async (postId: string) => {
    try {
      await api.rejectSocialPost(postId, rejectReason)
      if (activeCampaign) await fetchCampaign(activeCampaign.id)
      setReviewPost(null); setShowRejectModal(false); setRejectReason('')
    } catch { /* */ }
  }

  const handleSaveEdit = async () => {
    if (!editingPost) return
    try {
      await api.updateSocialPost(editingPost.id, { content: editContent })
      if (activeCampaign) await fetchCampaign(activeCampaign.id)
      setEditingPost(null)
    } catch { /* */ }
  }

  const handleDeleteCampaign = async (id: string) => {
    try {
      await api.deleteSocialCampaign(id)
      if (activeCampaign?.id === id) setActiveCampaign(null)
      fetchAll()
    } catch { /* */ }
  }

  const handleDeleteProject = async (id: string) => {
    try {
      await api.deleteSocialProject(id)
      if (activeProject?.id === id) setActiveProject(null)
      fetchAll()
    } catch { /* */ }
  }

  const openCampaign = (campaignId: string) => {
    setActiveProject(null)
    fetchCampaign(campaignId)
  }

  // =============================================================
  // RENDER: Post Review
  // =============================================================

  if (reviewPost) {
    return (
      <div className="social-view">
        <div className="social-review-modal">
          <div className="social-review-header">
            <button className="social-back-btn" onClick={() => setReviewPost(null)}><ArrowLeft size={18} /> Wstecz</button>
            <div className="social-review-platform" style={{ color: getPlatformColor(reviewPost.platform) }}>
              {getPlatformIcon(reviewPost.platform)} <span>{reviewPost.platform}</span>
            </div>
            <span className={`social-status-badge social-status-${reviewPost.status}`}>{STATUS_LABELS[reviewPost.status] || reviewPost.status}</span>
          </div>
          <div className="social-review-content">
            <div className="social-review-post-text">{reviewPost.content}</div>
            {reviewPost.hashtags?.length > 0 && (
              <div className="social-review-hashtags">{reviewPost.hashtags.map((h, i) => <span key={i} className="social-hashtag">{h}</span>)}</div>
            )}
            {reviewPost.image_prompt && (
              <div className="social-review-image-section">
                <h4><ImageIcon size={14} /> Prompt do obrazka</h4>
                <p className="social-image-prompt">{reviewPost.image_prompt}</p>
              </div>
            )}
            {reviewPost.scheduled_for && (
              <div className="social-review-schedule"><Calendar size={14} /><span>Zaplanowano: {new Date(reviewPost.scheduled_for).toLocaleString('pl-PL')}</span></div>
            )}
            {reviewPost.rejection_reason && (
              <div className="social-review-rejection"><XCircle size={14} /><span>Powód odrzucenia: {reviewPost.rejection_reason}</span></div>
            )}
          </div>
          {reviewPost.status === 'pending' && (
            <div className="social-review-actions">
              <button className="social-btn social-btn-approve" onClick={() => handleApprove(reviewPost.id)}><Check size={16} /> Akceptuj</button>
              <button className="social-btn social-btn-edit" onClick={() => { setEditingPost(reviewPost); setEditContent(reviewPost.content) }}><Edit3 size={16} /> Edytuj</button>
              <button className="social-btn social-btn-reject" onClick={() => setShowRejectModal(true)}><XCircle size={16} /> Odrzuć</button>
            </div>
          )}
          {showRejectModal && (
            <div className="social-reject-modal">
              <h4>Powód odrzucenia</h4>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Opisz dlaczego odrzucasz..." rows={3} />
              <div className="social-reject-actions">
                <button className="social-btn social-btn-reject" onClick={() => handleReject(reviewPost.id)}>Odrzuć</button>
                <button className="social-btn social-btn-secondary" onClick={() => { setShowRejectModal(false); setRejectReason('') }}>Anuluj</button>
              </div>
            </div>
          )}
          {editingPost && (
            <div className="social-edit-modal">
              <h4>Edytuj treść posta</h4>
              <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={8} />
              <div className="social-edit-actions">
                <button className="social-btn social-btn-approve" onClick={handleSaveEdit}>Zapisz</button>
                <button className="social-btn social-btn-secondary" onClick={() => setEditingPost(null)}>Anuluj</button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // =============================================================
  // RENDER: Campaign Detail
  // =============================================================

  if (activeCampaign) {
    const posts = activeCampaign.posts || []
    const filteredPosts = filterStatus === 'all' ? posts : posts.filter(p => p.status === filterStatus)
    const counts = { pending: posts.filter(p => p.status === 'pending').length, approved: posts.filter(p => p.status === 'approved').length, published: posts.filter(p => p.status === 'published').length, rejected: posts.filter(p => p.status === 'rejected').length }

    return (
      <div className="social-view">
        <div className="social-campaign-detail">
          <div className="social-detail-header">
            <button className="social-back-btn" onClick={() => { setActiveCampaign(null); fetchAll() }}><ArrowLeft size={18} /> Kampanie</button>
            <div className="social-detail-info">
              <h2>{activeCampaign.name || activeCampaign.topic}</h2>
              {activeCampaign.project_name && <span className="social-project-link" onClick={() => { setActiveCampaign(null); fetchProject(activeCampaign.social_project_id) }}><FolderOpen size={12} /> {activeCampaign.project_name}</span>}
              {activeCampaign.description && <p className="social-detail-desc">{activeCampaign.description}</p>}
              <div className="social-detail-meta">
                {activeCampaign.keywords?.length > 0 && <span className="social-meta-item"><Tag size={12} /> {activeCampaign.keywords.join(', ')}</span>}
                <span className="social-meta-item"><Target size={12} /> {GOAL_OPTIONS.find(g => g.value === activeCampaign.goal)?.label}</span>
                <span className="social-meta-item">{getModelShortName(activeCampaign.model)}</span>
                <span className="social-meta-item"><Calendar size={12} /> {FREQUENCY_OPTIONS.find(f => f.value === activeCampaign.schedule_frequency)?.label}</span>
                <span className="social-meta-item">{activeCampaign.platforms?.map((p: string) => <span key={p} style={{ marginRight: 4 }}>{getPlatformIcon(p)}</span>)}</span>
              </div>
            </div>
          </div>

          <div className="social-stats-bar">
            <div className="social-stat"><span className="social-stat-value">{posts.length}</span><span className="social-stat-label">Wygenerowanych</span></div>
            <div className="social-stat"><span className="social-stat-value social-stat-pending">{counts.pending}</span><span className="social-stat-label">Do akceptacji</span></div>
            <div className="social-stat"><span className="social-stat-value social-stat-approved">{counts.approved}</span><span className="social-stat-label">Zaakceptowanych</span></div>
            <div className="social-stat"><span className="social-stat-value social-stat-published">{counts.published}</span><span className="social-stat-label">Opublikowanych</span></div>
            {counts.rejected > 0 && <div className="social-stat"><span className="social-stat-value social-stat-rejected">{counts.rejected}</span><span className="social-stat-label">Odrzuconych</span></div>}
          </div>

          <div className="social-actions-bar">
            <button className="social-generate-btn" onClick={() => handleGenerate(activeCampaign.id)} disabled={generating}>
              {generating ? <><Loader2 size={14} className="spin" /> Generowanie...</> : <><Sparkles size={14} /> {posts.length > 0 ? 'Generuj nowe posty' : 'Generuj posty'}</>}
            </button>
            <div className="social-filter-tabs">
              <Filter size={14} />
              {['all', 'pending', 'approved', 'published', 'rejected'].map(s => (
                <button key={s} className={`social-filter-tab ${filterStatus === s ? 'active' : ''}`} onClick={() => setFilterStatus(s)}>
                  {s === 'all' ? 'Wszystkie' : STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {posts.length === 0 ? (
            <div className="social-empty-posts"><Send size={32} /><p>Kliknij "Generuj posty" aby Agent SocialAI stworzył pakiet postów do akceptacji</p></div>
          ) : filteredPosts.length === 0 ? (
            <div className="social-empty-posts"><Filter size={32} /><p>Brak postów o wybranym statusie</p></div>
          ) : (
            <div className="social-posts-list">
              {filteredPosts.map(post => (
                <div key={post.id} className={`social-post-card social-post-card--${post.status}`} onClick={() => setReviewPost(post)}>
                  <div className="social-post-card-left">
                    <div className="social-post-platform-badge" style={{ background: getPlatformColor(post.platform) + '20', color: getPlatformColor(post.platform) }}>{getPlatformIcon(post.platform)}</div>
                    <div className="social-post-card-info">
                      <p className="social-post-preview">{post.content.slice(0, 150)}{post.content.length > 150 ? '...' : ''}</p>
                      <div className="social-post-card-meta">
                        <span className={`social-status-badge social-status-${post.status}`}>{STATUS_LABELS[post.status]}</span>
                        {post.hashtags?.length > 0 && <span className="social-post-tag-count">{post.hashtags.length} hashtagów</span>}
                        {post.scheduled_for && <span className="social-post-schedule"><Clock size={11} /> {new Date(post.scheduled_for).toLocaleDateString('pl-PL')}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="social-post-card-actions">
                    {post.status === 'pending' && (
                      <>
                        <button className="social-action-btn social-action-approve" onClick={e => { e.stopPropagation(); handleApprove(post.id) }}><Check size={14} /></button>
                        <button className="social-action-btn social-action-reject" onClick={e => { e.stopPropagation(); setReviewPost(post); setShowRejectModal(true) }}><XCircle size={14} /></button>
                      </>
                    )}
                    <button className="social-action-btn" onClick={e => { e.stopPropagation(); setReviewPost(post) }}><Eye size={14} /></button>
                    <ChevronRight size={14} className="social-chevron" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // =============================================================
  // RENDER: Project Detail
  // =============================================================

  if (activeProject) {
    return (
      <div className="social-view">
        <div className="social-campaign-detail">
          <div className="social-detail-header">
            <button className="social-back-btn" onClick={() => { setActiveProject(null); fetchAll() }}><ArrowLeft size={18} /> Projekty</button>
            <div className="social-detail-info">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="tree-color-dot" style={{ backgroundColor: activeProject.icon_color, width: 12, height: 12, borderRadius: '50%' }} />
                {activeProject.name}
              </h2>
              {activeProject.description && <p className="social-detail-desc">{activeProject.description}</p>}
            </div>
          </div>

          <div className="social-form-section">
            <h3>Podłączone konta ({activeProject.accounts?.length || 0})</h3>
            <div className="social-accounts-list">
              {activeProject.accounts?.map(acc => (
                <div key={acc.id} className="social-account-item">
                  {getPlatformIcon(acc.platform)}
                  <span className="social-account-name">{acc.account_name}</span>
                  <span className={`social-account-status social-account-${acc.status}`}>{acc.status === 'connected' ? 'Połączony' : acc.status}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="social-form-section">
            <h3>Kampanie ({activeProject.campaigns?.length || 0})</h3>
            <div className="social-campaigns-list">
              {activeProject.campaigns?.map(c => (
                <div key={c.id} className="social-campaign-card" onClick={() => openCampaign(c.id)}>
                  <div className="social-campaign-card-left">
                    <Share2 size={18} className="social-campaign-icon" />
                    <div>
                      <h3 className="social-campaign-topic">{c.name}</h3>
                      <div className="social-campaign-meta">
                        <span>{c.platforms?.map((p: string) => <span key={p} style={{ marginRight: 2 }}>{getPlatformIcon(p, 12)}</span>)}</span>
                        <span>{c.generated_count} / {c.approved_count} / {c.published_count}</span>
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={16} style={{ color: 'var(--text-secondary)' }} />
                </div>
              ))}
              {(!activeProject.campaigns || activeProject.campaigns.length === 0) && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Brak kampanii w tym projekcie</p>
              )}
            </div>
          </div>

          <div className="social-project-actions" style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-lg)' }}>
            <button className="social-create-btn" onClick={() => startWizardFromProject(activeProject)}>
              <Plus size={14} /> Dodaj kampanię
            </button>
            <button className="social-btn social-btn-reject" onClick={() => handleDeleteProject(activeProject.id)}>
              <Trash2 size={14} /> Usuń projekt
            </button>
          </div>
        </div>
      </div>
    )
  }

  // =============================================================
  // RENDER: Wizard - Step 1: Accounts
  // =============================================================

  if (wizardStep === 'accounts') {
    return (
      <div className="social-view">
        <div className="social-wizard">
          <div className="social-wizard-header">
            <button className="social-back-btn" onClick={() => setWizardStep(null)}><ArrowLeft size={18} /> Anuluj</button>
            <div className="social-wizard-steps">
              <span className="social-wizard-step active">1. Konta</span>
              <ChevronRight size={14} />
              <span className="social-wizard-step">2. Projekt</span>
              <ChevronRight size={14} />
              <span className="social-wizard-step">3. Kampania</span>
            </div>
          </div>

          <h2>Wybierz lub dodaj konta social media</h2>
          <p className="social-desc">Zaznacz konta na które chcesz publikować posty w tej kampanii.</p>

          {/* Add new account */}
          <div className="social-accounts-panel" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <h3>Dodaj nowe konto</h3>
            <div className="social-form-grid">
              <label className="social-field">
                <span>Platforma</span>
                <select value={addAccountPlatform} onChange={e => setAddAccountPlatform(e.target.value)}>
                  {PLATFORM_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </label>
              <label className="social-field">
                <span>{addAccountPlatform === 'blog_api' ? 'Nazwa strony' : 'Nazwa konta / strony'}</span>
                <input type="text" value={addAccountName} onChange={e => setAddAccountName(e.target.value)} placeholder={addAccountPlatform === 'blog_api' ? 'np. Substraty.pl' : 'np. Moja Firma'} />
              </label>
              {addAccountPlatform === 'blog_api' ? (
                <>
                  <label className="social-field social-field--wide">
                    <span>URL endpointu API</span>
                    <input type="text" value={addAccountUrl} onChange={e => setAddAccountUrl(e.target.value)} placeholder="https://domena.pl/api_blog.php" />
                  </label>
                  <label className="social-field social-field--wide">
                    <span>Klucz API (X-Api-Key)</span>
                    <input type="password" value={addAccountApiKey} onChange={e => setAddAccountApiKey(e.target.value)} placeholder="Klucz autoryzacji API" />
                  </label>
                </>
              ) : (
                <>
                  <label className="social-field">
                    <span>Login / Email</span>
                    <input type="text" value={addAccountLogin} onChange={e => setAddAccountLogin(e.target.value)} placeholder="user@example.com" />
                  </label>
                  <label className="social-field">
                    <span>Haslo / Token</span>
                    <input type="password" value={addAccountPassword} onChange={e => setAddAccountPassword(e.target.value)} placeholder="••••••" />
                  </label>
                  <label className="social-field social-field--wide">
                    <span>URL profilu (opcjonalny)</span>
                    <input type="text" value={addAccountUrl} onChange={e => setAddAccountUrl(e.target.value)} placeholder="https://facebook.com/mojafirma" />
                  </label>
                </>
              )}
            </div>
            <button className="social-btn social-btn-approve" onClick={handleAddAccount} disabled={!addAccountName.trim() || (addAccountPlatform === 'blog_api' && (!addAccountUrl.trim() || !addAccountApiKey.trim()))} style={{ marginTop: 'var(--spacing-sm)' }}>
              <Plus size={14} /> Dodaj konto
            </button>
          </div>

          {/* Existing accounts */}
          <div className="social-accounts-select-list">
            {PLATFORM_OPTIONS.map(plat => {
              const platAccounts = accounts.filter(a => a.platform === plat.value)
              if (platAccounts.length === 0) return null
              return (
                <div key={plat.value} className="social-accounts-platform-group">
                  <h4>{getPlatformIcon(plat.value, 16)} {plat.label} ({platAccounts.length})</h4>
                  {platAccounts.map(acc => (
                    <label key={acc.id} className={`social-account-select-item ${wizardAccounts.includes(acc.id) ? 'selected' : ''}`}>
                      <input type="checkbox" checked={wizardAccounts.includes(acc.id)} onChange={e => {
                        setWizardAccounts(prev => e.target.checked ? [...prev, acc.id] : prev.filter(id => id !== acc.id))
                      }} />
                      <span className="social-account-name">{acc.account_name}</span>
                      {acc.login && <span className="social-account-login">{acc.login}</span>}
                      <span className={`social-account-status social-account-${acc.status}`}>
                        {acc.status === 'connected' ? <Wifi size={12} /> : <WifiOff size={12} />}
                      </span>
                      <button className="social-action-btn" onClick={e => { e.preventDefault(); handleTestAccount(acc.id) }} title="Test połączenia">
                        {testingAccount === acc.id ? <Loader2 size={12} className="spin" /> : <Wifi size={12} />}
                      </button>
                      <button className="social-action-btn social-action-reject" onClick={e => { e.preventDefault(); handleDeleteAccount(acc.id) }} title="Usuń">
                        <Trash2 size={12} />
                      </button>
                    </label>
                  ))}
                </div>
              )
            })}
            {accounts.length === 0 && <p className="social-accounts-empty">Dodaj pierwsze konto powyżej</p>}
          </div>

          <div className="social-wizard-footer">
            <button className="social-submit-btn" onClick={wizardGoToProject} disabled={wizardAccounts.length === 0}>
              Dalej: Wybierz projekt <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // =============================================================
  // RENDER: Wizard - Step 2: Project
  // =============================================================

  if (wizardStep === 'project') {
    return (
      <div className="social-view">
        <div className="social-wizard">
          <div className="social-wizard-header">
            <button className="social-back-btn" onClick={() => setWizardStep('accounts')}><ArrowLeft size={18} /> Wstecz</button>
            <div className="social-wizard-steps">
              <span className="social-wizard-step done">1. Konta</span>
              <ChevronRight size={14} />
              <span className="social-wizard-step active">2. Projekt</span>
              <ChevronRight size={14} />
              <span className="social-wizard-step">3. Kampania</span>
            </div>
          </div>

          <h2>Wybierz lub utwórz projekt</h2>
          <p className="social-desc">
            {wizardProjectId && projects.find(p => p.id === wizardProjectId)
              ? `Dodajesz nową kampanię do projektu "${projects.find(p => p.id === wizardProjectId)!.name}". Możesz zmienić projekt lub utworzyć nowy.`
              : 'Projekt grupuje konta i kampanie. Kampania będzie widoczna w wybranym projekcie.'}
          </p>

          {/* Existing projects */}
          <div className="social-projects-select-list">
            {projects.map(p => (
              <label key={p.id} className={`social-project-select-item ${wizardProjectId === p.id ? 'selected' : ''}`}>
                <input type="radio" name="project" checked={wizardProjectId === p.id} onChange={() => { setWizardProjectId(p.id); setNewProjectName('') }} />
                <div className="tree-color-dot" style={{ backgroundColor: p.icon_color, width: 10, height: 10, borderRadius: '50%' }} />
                <div>
                  <span className="social-project-select-name">{p.name}</span>
                  <span className="social-project-select-meta">
                    {p.accounts?.map(a => getPlatformIcon(a.platform, 11))} · {p.campaigns?.length || 0} kampanii
                  </span>
                </div>
              </label>
            ))}
          </div>

          <div className="social-new-project-form">
            <h4>Lub utwórz nowy projekt</h4>
            <label className={`social-project-select-item ${wizardProjectId === null && newProjectName.trim() ? 'selected' : ''}`}>
              <input type="radio" name="project" checked={wizardProjectId === null} onChange={() => setWizardProjectId(null)} />
              <input type="text" value={newProjectName} onChange={e => { setNewProjectName(e.target.value); setWizardProjectId(null) }}
                placeholder="Nazwa nowego projektu..." className="social-new-project-input" />
            </label>
          </div>

          <div className="social-wizard-footer">
            <button className="social-submit-btn" onClick={wizardGoToCampaign} disabled={!wizardProjectId && !newProjectName.trim()}>
              Dalej: Kreator kampanii <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // =============================================================
  // RENDER: Wizard - Step 3: Campaign
  // =============================================================

  if (wizardStep === 'campaign') {
    return (
      <div className="social-view">
        <div className="social-wizard">
          <div className="social-wizard-header">
            <button className="social-back-btn" onClick={() => setWizardStep('project')}><ArrowLeft size={18} /> Wstecz</button>
            <div className="social-wizard-steps">
              <span className="social-wizard-step done">1. Konta</span>
              <ChevronRight size={14} />
              <span className="social-wizard-step done">2. Projekt</span>
              <ChevronRight size={14} />
              <span className="social-wizard-step active">3. Kampania</span>
            </div>
          </div>

          <h2>Kreator kampanii</h2>

          <div className="social-create-form" style={{ border: 'none', padding: 0 }}>
            <div className="social-form-section">
              <h3>Treść kampanii</h3>
              <div className="social-form-grid">
                <label className="social-field social-field--wide"><span>Temat</span>
                  <input type="text" value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} placeholder="np. Najnowsze technologie biogazowe" />
                </label>
                <label className="social-field social-field--wide"><span>Opis (opcjonalny)</span>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Dodatkowy kontekst..." rows={2} />
                </label>
                <div className="social-field social-field--wide">
                  <span><Tag size={14} /> Słowa kluczowe <small style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(wpisz i wciśnij przecinek lub Enter)</small></span>
                  <div className="social-input-with-btn">
                    <input type="text" value={keywordInput} onChange={handleKeywordInput} onKeyDown={handleKeywordKeyDown} placeholder="Wpisz słowo kluczowe..." />
                  </div>
                  {form.keywords.length > 0 && (
                    <div className="social-tags-list">{form.keywords.map(kw => (
                      <span key={kw} className="social-tag">{kw} <button onClick={() => setForm(f => ({ ...f, keywords: f.keywords.filter(k => k !== kw) }))}><X size={12} /></button></span>
                    ))}</div>
                  )}
                </div>
                <div className="social-field social-field--wide">
                  <span><LinkIcon size={14} /> Źródła informacji</span>
                  <div className="social-input-with-btn">
                    <input type="text" value={sourceInput} onChange={e => setSourceInput(e.target.value)} placeholder="URL lub opis źródła..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSource() } }} />
                    <button type="button" onClick={handleAddSource}><Plus size={14} /></button>
                  </div>
                  {form.info_sources.length > 0 && (
                    <div className="social-tags-list">{form.info_sources.map(src => (
                      <span key={src} className="social-tag social-tag-source">{src} <button onClick={() => setForm(f => ({ ...f, info_sources: f.info_sources.filter(s => s !== src) }))}><X size={12} /></button></span>
                    ))}</div>
                  )}
                </div>
                <label className="social-field"><span>Liczba postów</span>
                  <input type="number" min="1" max="50" value={form.target_count} onChange={e => setForm(f => ({ ...f, target_count: e.target.value }))} />
                </label>
              </div>
            </div>

            <div className="social-form-section">
              <h3><Target size={16} /> Cel postów</h3>
              <div className="social-goal-grid">
                {GOAL_OPTIONS.map(g => (
                  <button key={g.value} className={`social-goal-card ${form.goal === g.value ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, goal: g.value }))}>
                    <span className="social-goal-icon">{g.icon}</span>
                    <span className="social-goal-label">{g.label}</span>
                    <span className="social-goal-desc">{g.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="social-form-section">
              <h3>Platformy docelowe</h3>
              <div className="social-platform-grid">
                {form.platforms.map(p => {
                  const opt = PLATFORM_OPTIONS.find(o => o.value === p)
                  return opt ? (
                    <div key={p} className="social-platform-card active" style={{ '--platform-color': opt.color } as any}>
                      {getPlatformIcon(p, 20)} <span>{opt.label}</span>
                    </div>
                  ) : null
                })}
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', marginTop: 'var(--spacing-xs)' }}>Platformy wynikają z wybranych kont w kroku 1</p>
            </div>

            <div className="social-form-section">
              <h3><Calendar size={16} /> Harmonogram</h3>
              <div className="social-form-grid">
                <label className="social-field"><span>Częstotliwość</span>
                  <select value={form.schedule_frequency} onChange={e => setForm(f => ({ ...f, schedule_frequency: e.target.value }))}>
                    {FREQUENCY_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </label>
                <label className="social-field"><span>Tryb godziny</span>
                  <select value={form.schedule_time_mode} onChange={e => setForm(f => ({ ...f, schedule_time_mode: e.target.value }))}>
                    <option value="fixed">Stała godzina</option>
                    <option value="range">Przedział godzin</option>
                  </select>
                </label>
                {form.schedule_time_mode === 'fixed' ? (
                  <label className="social-field"><span>Godzina publikacji</span><input type="time" value={form.schedule_fixed_hour} onChange={e => setForm(f => ({ ...f, schedule_fixed_hour: e.target.value }))} /></label>
                ) : (
                  <>
                    <label className="social-field"><span>Od godziny</span><input type="time" value={form.schedule_range_start} onChange={e => setForm(f => ({ ...f, schedule_range_start: e.target.value }))} /></label>
                    <label className="social-field"><span>Do godziny</span><input type="time" value={form.schedule_range_end} onChange={e => setForm(f => ({ ...f, schedule_range_end: e.target.value }))} /></label>
                  </>
                )}
              </div>
            </div>

            <div className="social-form-section">
              <h3>Model AI</h3>
              {hasProviders ? (
                <div className="social-form-grid">
                  <label className="social-field"><span>Provider</span>
                    <select value={form.provider} onChange={e => {
                      const prov = e.target.value
                      const models = availableModels?.find(p => p.provider_type === prov)?.models || []
                      setForm(f => ({ ...f, provider: prov, model: models[0] || '' }))
                    }}>
                      <option value="">-- Wybierz provider --</option>
                      {availableModels?.map(p => (
                        <option key={p.provider_type} value={p.provider_type}>{p.display_name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="social-field"><span>Model</span>
                    <select value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} disabled={!form.provider}>
                      <option value="">-- Wybierz model --</option>
                      {selectedProviderModels.map(m => (
                        <option key={m} value={m}>{getModelShortName(m)}</option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : (
                <div className="agent-no-provider">
                  <ShoppingCart size={18} />
                  <div>
                    <p>Nie masz skonfigurowanego klucza API.</p>
                    <p>Dodaj wlasny klucz w ustawieniach lub wypozycz na gieldzie.</p>
                  </div>
                  <button className="agent-marketplace-btn" onClick={navigateToMarketplace}>
                    <ShoppingCart size={14} /> Przejdz do Gieldy API
                  </button>
                </div>
              )}
            </div>

            <button className="social-submit-btn" onClick={handleCreateCampaign} disabled={!form.topic.trim()}>
              <Sparkles size={14} /> Utwórz kampanię
            </button>
          </div>
        </div>
      </div>
    )
  }

  // =============================================================
  // RENDER: Manage Accounts (standalone)
  // =============================================================

  if (showManageAccounts) {
    return (
      <div className="social-view">
        <div className="social-detail-header">
          <button className="social-back-btn" onClick={() => { setShowManageAccounts(false); fetchAll() }}><ArrowLeft size={18} /> Wstecz</button>
          <div className="social-detail-info"><h2>Zarządzanie kontami</h2></div>
        </div>

        <div className="social-accounts-panel" style={{ marginTop: 'var(--spacing-lg)' }}>
          <h3>Dodaj nowe konto</h3>
          <div className="social-form-grid">
            <label className="social-field"><span>Platforma</span><select value={addAccountPlatform} onChange={e => setAddAccountPlatform(e.target.value)}>{PLATFORM_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</select></label>
            <label className="social-field"><span>Nazwa konta</span><input type="text" value={addAccountName} onChange={e => setAddAccountName(e.target.value)} placeholder={addAccountPlatform === 'blog_api' ? 'np. Substraty.pl' : 'np. Moja Firma'} /></label>
            {addAccountPlatform === 'blog_api' ? (
              <>
                <label className="social-field social-field--wide"><span>URL endpointu API</span><input type="text" value={addAccountUrl} onChange={e => setAddAccountUrl(e.target.value)} placeholder="https://domena.pl/api_blog.php" /></label>
                <label className="social-field social-field--wide"><span>Klucz API (X-Api-Key)</span><input type="password" value={addAccountApiKey} onChange={e => setAddAccountApiKey(e.target.value)} placeholder="Klucz autoryzacji API" /></label>
              </>
            ) : (
              <>
                <label className="social-field"><span>Login / Email</span><input type="text" value={addAccountLogin} onChange={e => setAddAccountLogin(e.target.value)} /></label>
                <label className="social-field"><span>Haslo / Token</span><input type="password" value={addAccountPassword} onChange={e => setAddAccountPassword(e.target.value)} /></label>
                <label className="social-field social-field--wide"><span>URL profilu</span><input type="text" value={addAccountUrl} onChange={e => setAddAccountUrl(e.target.value)} /></label>
              </>
            )}
          </div>
          <button className="social-btn social-btn-approve" onClick={handleAddAccount} disabled={!addAccountName.trim() || (addAccountPlatform === 'blog_api' && (!addAccountUrl.trim() || !addAccountApiKey.trim()))} style={{ marginTop: 'var(--spacing-sm)' }}><Plus size={14} /> Dodaj</button>
        </div>

        <div style={{ marginTop: 'var(--spacing-xl)' }}>
          {PLATFORM_OPTIONS.map(plat => {
            const platAccounts = accounts.filter(a => a.platform === plat.value)
            if (platAccounts.length === 0) return null
            return (
              <div key={plat.value} className="social-accounts-platform-group">
                <h4>{getPlatformIcon(plat.value, 16)} {plat.label} ({platAccounts.length})</h4>
                {platAccounts.map(acc => (
                  <div key={acc.id} className="social-account-item">
                    {getPlatformIcon(acc.platform)}
                    <span className="social-account-name">{acc.account_name}</span>
                    {acc.platform === 'blog_api'
                      ? acc.profile_url && <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{acc.profile_url}</span>
                      : acc.login && <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{acc.login}</span>
                    }
                    <span className={`social-account-status social-account-${acc.status}`}>{acc.status === 'connected' ? <Wifi size={12} /> : <WifiOff size={12} />}</span>
                    <button className="social-action-btn" onClick={() => handleTestAccount(acc.id)}>{testingAccount === acc.id ? <Loader2 size={12} className="spin" /> : <Wifi size={12} />}</button>
                    <button className="social-action-btn social-action-reject" onClick={() => handleDeleteAccount(acc.id)}><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // =============================================================
  // RENDER: Main View — Campaigns + Projects
  // =============================================================

  return (
    <div className="social-view">
      <div className="social-header">
        <div className="social-title-row">
          <Share2 size={24} className="social-icon" />
          <div>
            <h1>Agent SocialAI</h1>
            <p className="social-desc">Automatyczne tworzenie i planowanie postów na social media</p>
          </div>
        </div>
        <div className="social-header-actions">
          <button className="social-accounts-btn" onClick={() => setShowManageAccounts(true)}>
            <Settings size={16} /> Konta ({accounts.length})
          </button>
          <button className="social-create-btn" onClick={startWizard}>
            <Plus size={16} /> Nowa kampania
          </button>
        </div>
      </div>

      {/* Projects */}
      {projects.length > 0 && (
        <div className="social-section">
          <h3 className="social-section-title"><FolderOpen size={16} /> Projekty</h3>
          <div className="social-projects-grid">
            {projects.map(p => (
              <div key={p.id} className="social-project-card" onClick={() => fetchProject(p.id)}>
                <div className="social-project-card-header">
                  <div className="tree-color-dot" style={{ backgroundColor: p.icon_color, width: 10, height: 10, borderRadius: '50%' }} />
                  <h4>{p.name}</h4>
                </div>
                <div className="social-project-card-platforms">
                  {p.accounts?.map(a => <span key={a.id}>{getPlatformIcon(a.platform, 13)}</span>)}
                </div>
                <div className="social-project-card-meta">
                  {p.accounts?.length || 0} kont · {p.campaigns?.length || 0} kampanii
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Campaigns */}
      <div className="social-section">
        <h3 className="social-section-title"><Send size={16} /> Kampanie</h3>
        <div className="social-campaigns-list">
          {loading && <p className="social-loading"><Loader2 size={18} className="spin" /> Ładowanie...</p>}
          {!loading && campaigns.length === 0 && (
            <div className="social-empty">
              <Share2 size={48} />
              <h3>Brak kampanii</h3>
              <p>Utwórz nową kampanię, aby Agent SocialAI zaczął generować posty.</p>
              <button className="social-create-btn" onClick={startWizard}><Plus size={16} /> Nowa kampania</button>
            </div>
          )}
          {campaigns.map(c => (
            <div key={c.id} className="social-campaign-card" onClick={() => openCampaign(c.id)}>
              <div className="social-campaign-card-left">
                <Share2 size={20} className="social-campaign-icon" />
                <div>
                  <h3 className="social-campaign-topic">{c.name || c.topic}</h3>
                  <div className="social-campaign-meta">
                    <span>{c.platforms?.map((p: string) => <span key={p} style={{ marginRight: 2 }}>{getPlatformIcon(p, 12)}</span>)}</span>
                    {c.project_name && <span><FolderOpen size={11} /> {c.project_name}</span>}
                    <span>{GOAL_OPTIONS.find(g => g.value === c.goal)?.icon} {GOAL_OPTIONS.find(g => g.value === c.goal)?.label}</span>
                    <span>{c.generated_count} post. / {c.approved_count} akc. / {c.published_count} pub.</span>
                  </div>
                </div>
              </div>
              <div className="social-campaign-card-right">
                <span className={`social-campaign-status social-campaign-status--${c.status}`}>{c.status === 'active' ? 'Aktywna' : c.status === 'draft' ? 'Szkic' : c.status}</span>
                <span className="social-campaign-date">{new Date(c.created_at).toLocaleDateString('pl-PL')}</span>
                <button className="social-delete-btn" onClick={e => { e.stopPropagation(); handleDeleteCampaign(c.id) }}><Trash2 size={14} /></button>
                <ChevronRight size={16} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
